import { http } from '@/services/api';

export interface TeamDTO {
  id: number;
  name: string;
  description?: string | null;
  region_id: number;
  leader_id: number;
  meeting_day?: string | null;
  meeting_time?: string | null;
  is_active: boolean;
  next_schedule_days?: number | null;
  created_at: string;
  updated_at: string;
  members_count?: number;
  region?: { id: number; name: string; uf: string };
  leader?: { id: number; name: string; email: string };
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number | string;
  total: number;
}

export interface TeamFilters {
  search?: string;
  region_id?: number;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface TeamPayload {
  name?: string;
  description?: string | null;
  region_id?: number;
  leader_id?: number;
  meeting_day?: string | null;
  meeting_time?: string | null;
  is_active?: boolean;
  next_schedule_days?: number | null;
}

export const teamsService = {
  async getById(id: number): Promise<TeamDTO> {
    const response = await http.get<TeamDTO>(`/teams/${id}`);
    return response.data;
  },

  async list(filters: TeamFilters = {}): Promise<Paginated<TeamDTO>> {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.region_id) params.set('region_id', String(filters.region_id));
    if (filters.page) params.set('page', String(filters.page));
    if (filters.per_page) params.set('per_page', String(filters.per_page));
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    if (filters.sort_dir) params.set('sort_dir', filters.sort_dir);
    return http.get<Paginated<TeamDTO>>(`/teams?${params.toString()}`);
  },

  // Lightweight non-paginated list for dropdowns and simple selectors
  async listAll(): Promise<TeamDTO[]> {
    return http.get<TeamDTO[]>(`/teams/list`);
  },

  // Convenience: get all teams and filter by region on the client
  async listByRegion(region_id: number): Promise<TeamDTO[]> {
    const all = await this.listAll();
    return all.filter(t => t.region_id === region_id);
  },

  async create(payload: Required<Pick<TeamPayload, 'name' | 'region_id' | 'leader_id'>> & Omit<TeamPayload, 'name' | 'region_id' | 'leader_id'>): Promise<TeamDTO> {
    return http.post<TeamDTO>('/teams', payload);
  },

  async update(id: number, payload: TeamPayload): Promise<TeamDTO> {
    return http.put<TeamDTO>(`/teams/${id}`, payload);
  },

  async remove(id: number): Promise<{ message: string }> {
    return http.delete<{ message: string }>(`/teams/${id}`);
  },

  async addMembers(id: number, user_ids: number[]): Promise<unknown> {
    return http.post<unknown>(`/teams/${id}/members`, { user_ids });
  },

  async removeMembers(id: number, user_ids: number[]): Promise<unknown> {
    // Using POST to a dedicated endpoint to send a body
    return http.post<unknown>(`/teams/${id}/members/remove`, { user_ids });
  },

  // List current members of a team (excluding leader)
  async listMembers(id: number): Promise<Array<{ id: number; name: string; email?: string }>> {
    return http.get<Array<{ id: number; name: string; email?: string }>>(`/teams/${id}/members`);
  },

  // Replace/synchronize members list (excluding leader). Backend should handle diff.
  async updateMembers(id: number, user_ids: number[]): Promise<{ message?: string }> {
    return http.put<{ message?: string }>(`/teams/${id}/members`, { user_ids });
  },

  async statistics(id: number): Promise<unknown> {
    return http.get<unknown>(`/teams/${id}/statistics`);
  },

  // Current user's teams
  async my(): Promise<TeamDTO[]> {
    return http.get<TeamDTO[]>(`/teams/my`);
  },

  // Get current user's active team id from profile
  async activeId(): Promise<number | null> {
    type UserShape = { id: number; name: string; email: string; active_team_id?: number | null };
    type Resp = UserShape | { data: UserShape };
    const res = await http.get<Resp>(`/user`);
    const user: UserShape = (res as { data?: UserShape }).data ? (res as { data: UserShape }).data : (res as UserShape);
    return user?.active_team_id ?? null;
  },

  // Activate a team for the current user (single active team per user)
  async activate(teamId: number): Promise<{ message: string; active_team_id: number }> {
    return http.post<{ message: string; active_team_id: number }>(`/teams/${teamId}/activate`, {});
  }
};
